/**
 * useOutcomeDetail — Level 2 data for one outcome.
 *
 * GET /v1/datasets/{id}/outcomes-report/outcomes/{outcomeId}
 *       ?framework=cd|tcf&grain=daily|weekly|monthly&<filters>
 *
 * The API buckets the series at the requested grain, so changing grain refetches
 * rather than re-bucketing client-side.
 */

import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import type { FilterConfig } from '../../../records/filter';
import { fetchWithAuth } from '../../../../../utils/api';
import { mapFiltersToApiParams } from '../../cause-report/utils';
import { cleanOutcomeName } from '../utils';
import { MONITORING_STALE_TIME, monitoringKeys } from '../constants';
import type {
  BandRecords,
  Bands,
  DetailData,
  DetrimentLabel,
  DetrimentMix,
  Framework,
  Grain,
  HighBandRecord,
  OutcomeHeader,
  SeriesPoint,
} from '../types';

interface BandsApi {
  low: number;
  mod: number;
  high: number;
}
interface DetrimentMixApi {
  dissatisfaction: number;
  strain: number;
  harm: number;
}
interface SeriesPointApi {
  bucket_start: string;
  label: string;
  volume: number;
  score: number;
  bands: BandsApi;
  detriment: DetrimentMixApi;
}
interface DetrimentLabelApi {
  type: DetrimentLabel['type'];
  label_id: string;
  name: string;
  volume: number;
  by_bucket: Record<string, number>;
}
interface HighBandRecordApi {
  id: string;
  score: number;
  headline: string;
  channel: string | null;
  occurred_at: string | null;
  outcome_id: string;
  outcome_name: string;
}
interface OutcomeHeaderApi {
  id: string;
  name: string;
  score: number;
  score_prev: number | null;
  volume: number;
  volume_prev: number | null;
  bands: BandsApi;
}
interface BandRecordsApi {
  low: HighBandRecordApi[];
  mod: HighBandRecordApi[];
  high: HighBandRecordApi[];
}
interface DetailApiResponse {
  outcome: OutcomeHeaderApi;
  series: SeriesPointApi[];
  detriment_labels: DetrimentLabelApi[];
  band_records: BandRecordsApi;
}

const bands = (b: BandsApi): Bands => ({ low: b.low, mod: b.mod, high: b.high });
const mix = (m: DetrimentMixApi): DetrimentMix => ({
  dissatisfaction: m.dissatisfaction,
  strain: m.strain,
  harm: m.harm,
});

function transform(data: DetailApiResponse): DetailData {
  const outcome: OutcomeHeader = {
    id: data.outcome.id,
    name: cleanOutcomeName(data.outcome.name),
    score: data.outcome.score,
    scorePrev: data.outcome.score_prev,
    volume: data.outcome.volume,
    volumePrev: data.outcome.volume_prev,
    bands: bands(data.outcome.bands),
  };
  const series: SeriesPoint[] = data.series.map((p) => ({
    bucketStart: p.bucket_start,
    label: p.label,
    volume: p.volume,
    score: p.score,
    bands: bands(p.bands),
    detriment: mix(p.detriment),
  }));
  const detrimentLabels: DetrimentLabel[] = data.detriment_labels.map((l) => ({
    type: l.type,
    labelId: l.label_id,
    name: l.name,
    volume: l.volume,
    byBucket: l.by_bucket ?? {},
  }));
  const mapRecord = (r: HighBandRecordApi): HighBandRecord => ({
    id: r.id,
    score: r.score,
    headline: r.headline,
    channel: r.channel,
    occurredAt: r.occurred_at,
    outcomeId: r.outcome_id,
    outcomeName: cleanOutcomeName(r.outcome_name),
  });
  const bandRecords: BandRecords = {
    low: data.band_records.low.map(mapRecord),
    mod: data.band_records.mod.map(mapRecord),
    high: data.band_records.high.map(mapRecord),
  };
  return { outcome, series, detrimentLabels, bandRecords };
}

async function fetchDetail(
  datasetId: string,
  framework: Framework,
  outcomeId: string,
  grain: Grain,
  params: Record<string, string>,
): Promise<DetailApiResponse> {
  const qs = new URLSearchParams({ framework, grain, ...params }).toString();
  return fetchWithAuth(
    `/v1/datasets/${datasetId}/outcomes-report/outcomes/${outcomeId}?${qs}`,
  );
}

export function useOutcomeDetail(
  datasetId: string,
  framework: Framework,
  outcomeId: string | null,
  grain: Grain,
  filters: FilterConfig,
) {
  const filterParams = useMemo(() => mapFiltersToApiParams(filters), [filters]);

  const query = useQuery({
    queryKey: monitoringKeys.detail(datasetId, framework, outcomeId ?? '', grain, filterParams),
    queryFn: () => fetchDetail(datasetId, framework, outcomeId as string, grain, filterParams),
    select: transform,
    staleTime: MONITORING_STALE_TIME,
    enabled: !!datasetId && !!outcomeId,
    // Keep the previous grain/filter's data visible while the new one loads, so
    // the detail view dims-and-refreshes rather than blanking to a skeleton.
    placeholderData: keepPreviousData,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    // See useMonitoringOverview: true during a background refetch (filter change)
    // so the detail view can show a refreshing state rather than stale figures.
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
